import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Grid, Chip,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import {
  Shield, SupportAgent,
  Storage, Speed, Lock, Groups,
  Business, CheckCircle, ArrowForward, ExpandMore,
  VpnKey, AdminPanelSettings, CloudSync, Security,
  VerifiedUser, ManageAccounts, DomainVerification, Backup,
  AccountBalance, LocalHospital, School, Factory,
  Gavel, TrendingUp, Api, Palette,
} from '@mui/icons-material';
import PublicLayout from '../components/layout/PublicLayout';

const stats = [
  { value: '500+', label: 'Organizations' },
  { value: '99.99%', label: 'Uptime SLA' },
  { value: '24/7', label: 'Priority Support' },
  { value: '< 1hr', label: 'Incident Response' },
];

const pillars = [
  {
    color: '#dc2626', icon: <Shield sx={{ fontSize: 30, color: '#fff' }} />,
    title: 'Security & Compliance',
    desc: 'All email runs through our secured datacenter infrastructure with end-to-end TLS 1.3, DKIM/SPF/DMARC, Rspamd spam filter, 2FA, IP allowlisting, and full audit logging.',
    points: ['Custom spam scoring & quarantine rules', 'Org-wide 2FA policy enforcement', 'Full admin audit trail', 'Role-based access control (RBAC)'],
  },
  {
    color: '#1565c0', icon: <AdminPanelSettings sx={{ fontSize: 30, color: '#fff' }} />,
    title: 'Full Admin Control',
    desc: 'Manage every user, domain, mailbox, quota, alias, and delivery policy from a single admin console. No server login needed — everything is in the dashboard.',
    points: ['Multi-domain management from one panel', 'Bulk user provisioning via CSV', 'Per-domain quotas & retention policies', 'Live mail traffic & delivery logs'],
  },
  {
    color: '#7c3aed', icon: <Palette sx={{ fontSize: 30, color: '#fff' }} />,
    title: 'White-Label & Branding',
    desc: 'Your users see your logo, your colors, and your domain — not CruvzMail. The login page, webmail, and emails are fully branded to your organization.',
    points: ['Upload logo & set brand colors', 'Custom login page (your domain)', 'No CruvzMail branding visible anywhere', 'Per-domain branding configurations'],
  },
];

const planTiers = [
  {
    tag: 'Business', color: '#1565c0', recommended: false,
    title: 'Business Plan',
    desc: 'For growing teams that need professional business email with full admin control and custom domain.',
    points: ['Up to 50 mailboxes', 'Custom domain email', 'Admin console access', 'Email + chat support'],
  },
  {
    tag: 'Professional', color: '#dc2626', recommended: true,
    title: 'Professional Plan',
    desc: 'For mid-size organizations needing higher limits, white-label branding, and priority support with SLA.',
    points: ['Up to 500 mailboxes', 'Full white-label branding', 'Priority support (< 4hr response)', 'Dedicated onboarding manager'],
  },
  {
    tag: 'Enterprise', color: '#7c3aed', recommended: false,
    title: 'Enterprise Plan',
    desc: 'Custom limits, dedicated resources, a named support engineer, and a formal SLA contract for large organizations.',
    points: ['Unlimited mailboxes & domains', 'Dedicated infrastructure in our datacenter', 'Named support engineer + phone hotline', 'Custom SLA & compliance contracts'],
  },
];

const industries = [
  { icon: <AccountBalance sx={{ fontSize: 28 }} />, label: 'Finance & Banking', color: '#1565c0' },
  { icon: <LocalHospital sx={{ fontSize: 28 }} />, label: 'Healthcare', color: '#dc2626' },
  { icon: <Gavel sx={{ fontSize: 28 }} />, label: 'Legal & Compliance', color: '#7c3aed' },
  { icon: <School sx={{ fontSize: 28 }} />, label: 'Education', color: '#0891b2' },
  { icon: <Factory sx={{ fontSize: 28 }} />, label: 'Manufacturing', color: '#d97706' },
  { icon: <AdminPanelSettings sx={{ fontSize: 28 }} />, label: 'Government', color: '#059669' },
  { icon: <TrendingUp sx={{ fontSize: 28 }} />, label: 'Startups & SaaS', color: '#dc2626' },
  { icon: <Api sx={{ fontSize: 28 }} />, label: 'Technology', color: '#1565c0' },
];

const complianceItems = [
  { icon: <VerifiedUser />, label: 'GDPR Ready', desc: 'Data residency controls, DPA support, right-to-erasure built in' },
  { icon: <Shield />, label: 'HIPAA Aligned', desc: 'Encrypted storage, full audit logs, BAA available on request' },
  { icon: <DomainVerification />, label: 'SPF / DKIM / DMARC', desc: 'Auto-configured per domain, full DMARC reporting included' },
  { icon: <Lock />, label: 'TLS 1.3 in Transit', desc: 'All API and mail connections enforce TLS 1.3 — no exceptions' },
  { icon: <VpnKey />, label: '2FA Enforcement', desc: 'Admin can enforce 2FA org-wide, per-domain, or per-user' },
  { icon: <Backup />, label: 'Audit Logging', desc: 'Full tamper-evident admin action log, exportable any time' },
  { icon: <ManageAccounts />, label: 'RBAC', desc: 'Superadmin, admin, and user roles with granular permissions' },
  { icon: <Security />, label: 'Spam & Phishing Filter', desc: 'Rspamd with custom scoring, quarantine, and real-time blocklists' },
];

const supportTiers = [
  {
    tier: 'Business', color: '#64748b', highlight: false,
    response: '< 24 hours', sla: '99.9%',
    features: ['Email + chat support', 'Documentation portal', 'Standard platform updates'],
  },
  {
    tier: 'Professional', color: '#dc2626', highlight: true,
    response: '< 4 hours', sla: '99.95%',
    features: ['Dedicated onboarding manager', 'Priority email + phone support', 'Incident escalation path', 'Monthly account review'],
  },
  {
    tier: 'Enterprise', color: '#7c3aed', highlight: false,
    response: '< 1 hour', sla: '99.99%',
    features: ['Named support engineer', 'Phone hotline & Slack channel', 'Custom SLA contract', 'Quarterly business review', 'Training & team certification'],
  },
];

const faqs = [
  { q: 'Where is CruvzMail hosted?', a: 'CruvzMail is hosted entirely within our own secured datacenter infrastructure. We manage all servers, backups, and uptime — you just connect your domain and start sending.' },
  { q: 'Can we use our own domain (user@ourcompany.com)?', a: 'Yes. Every plan includes custom domain email. You point your MX records to us, and your team gets professional email on your own domain — fully hosted.' },
  { q: 'Do you support multi-domain organizations?', a: 'Yes. You can manage multiple domains from a single admin console. Each domain gets independent DKIM keys, branding, catch-all rules, and user management.' },
  { q: 'Can we white-label the webmail completely?', a: 'Yes — upload your logo, set brand colors, and customize the login page URL. Your users see zero CruvzMail branding. Available on Professional and Enterprise plans.' },
  { q: 'What compliance frameworks do you support?', a: 'CruvzMail is GDPR-ready with data residency controls and DPA support. Healthcare customers can receive a BAA for HIPAA alignment. Full audit logs and right-to-erasure are built in.' },
  { q: 'Do you offer volume pricing for large teams?', a: 'Yes. Contact our sales team for custom pricing based on mailbox count, storage needs, and support tier. Volume discounts start from 50+ mailboxes.' },
];

const EnterprisePage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <PublicLayout activeNav="For Business">

      {/* ── HERO ── */}
      <Box sx={{
        pt: { xs: '120px', md: '145px' }, pb: { xs: 10, md: 14 }, px: { xs: 2, md: 5 },
        position: 'relative', overflow: 'hidden', textAlign: 'center',
        background: 'linear-gradient(160deg, #0f172a 0%, #1e1b4b 50%, #1a0000 100%)',
      }}>
        <Box sx={{ position: 'absolute', top: -180, right: -120, width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(220,38,38,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: -80, left: -80, width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.1) 0%, transparent 70%)', pointerEvents: 'none' }} />

        <Box sx={{ maxWidth: 880, mx: 'auto', position: 'relative', zIndex: 1 }}>
          <Chip label="For Business" sx={{ mb: 3, bgcolor: 'rgba(220,38,38,0.15)', color: '#fca5a5', fontWeight: 700, fontSize: '0.78rem', border: '1px solid rgba(220,38,38,0.3)', letterSpacing: '0.06em' }} />
          <Typography variant="h1" sx={{
            fontWeight: 900, fontSize: { xs: '2.4rem', md: '3.8rem' }, lineHeight: 1.1, mb: 3,
            background: 'linear-gradient(135deg, #ffffff 30%, #fca5a5 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
          }}>
            Professional Email for<br />Your Entire Organization
          </Typography>
          <Typography sx={{ fontSize: { xs: '1rem', md: '1.2rem' }, color: 'rgba(255,255,255,0.6)', maxWidth: 640, mx: 'auto', lineHeight: 1.85, mb: 5 }}>
            Get your team up and running on your own domain — fully hosted in our secure datacenter. Custom branding, admin controls, compliance tools, and priority support included.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button onClick={() => navigate('/contact')} endIcon={<ArrowForward />} sx={{ px: 4, py: 1.6, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none', color: '#fff', bgcolor: '#dc2626', boxShadow: '0 0 40px rgba(220,38,38,0.4)', '&:hover': { bgcolor: '#b91c1c' } }}>
              Contact Sales
            </Button>
            <Button onClick={() => navigate('/pricing')} sx={{ px: 4, py: 1.6, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', bgcolor: 'rgba(255,255,255,0.05)', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
              View Pricing Plans
            </Button>
          </Box>
        </Box>
      </Box>

      {/* ── STATS ── */}
      <Box sx={{ bgcolor: '#0f172a', py: 3.5, px: { xs: 2, md: 5 }, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <Box sx={{ maxWidth: 900, mx: 'auto' }}>
          <Grid container spacing={2} justifyContent="center">
            {stats.map((s) => (
              <Grid size={{ xs: 6, md: 3 }} key={s.label}>
                <Box sx={{ textAlign: 'center', py: 1 }}>
                  <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.6rem', md: '2rem' }, color: '#fff', lineHeight: 1 }}>{s.value}</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', fontWeight: 500, mt: 0.4 }}>{s.label}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* ── WHAT YOUR ORGANIZATION GETS ── */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: { xs: 2, md: 5 }, bgcolor: '#f8fafc' }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: 7 }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#94a3b8', mb: 1.5 }}>Everything You Need</Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', fontSize: { xs: '1.8rem', md: '2.4rem' }, mb: 1.5 }}>
              What Your Organization Gets
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '1rem', maxWidth: 520, mx: 'auto' }}>
              Security, admin control, and custom branding — fully managed on CruvzMail's datacenter infrastructure.
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {pillars.map((p, i) => (
              <Grid size={{ xs: 12, md: 4 }} key={p.title}>
                <Box sx={{
                  height: '100%', p: { xs: 3, md: 4 }, borderRadius: 3.5,
                  border: '1px solid #e2e8f0', bgcolor: '#fff',
                  position: 'relative', overflow: 'hidden',
                  transition: 'all 0.25s',
                  '&:hover': { borderColor: p.color, boxShadow: `0 8px 40px ${p.color}12`, transform: 'translateY(-3px)' },
                }}>
                  <Typography sx={{ position: 'absolute', top: -14, right: 14, fontWeight: 900, fontSize: '5.5rem', color: `${p.color}09`, lineHeight: 1, userSelect: 'none', pointerEvents: 'none' }}>
                    {String(i + 1).padStart(2, '0')}
                  </Typography>
                  <Box sx={{ width: 50, height: 50, borderRadius: 2.5, bgcolor: p.color, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5, boxShadow: `0 6px 20px ${p.color}30` }}>
                    {p.icon}
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', color: '#0f172a', mb: 1.2 }}>{p.title}</Typography>
                  <Typography sx={{ fontSize: '0.88rem', color: '#64748b', lineHeight: 1.8, mb: 2.5 }}>{p.desc}</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.9 }}>
                    {p.points.map((pt) => (
                      <Box key={pt} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircle sx={{ fontSize: 15, color: p.color, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.82rem', color: '#475569', fontWeight: 500 }}>{pt}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* ── PLANS ── */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: { xs: 2, md: 5 }, bgcolor: '#fff' }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: 7 }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#94a3b8', mb: 1.5 }}>Plans</Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', fontSize: { xs: '1.8rem', md: '2.4rem' }, mb: 1.5 }}>
              Plans for Every Business Size
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '1rem', maxWidth: 520, mx: 'auto' }}>
              From small teams to large enterprises — fully managed cloud email on CruvzMail's secure infrastructure.
            </Typography>
          </Box>
          <Grid container spacing={3} alignItems="stretch">
            {planTiers.map((d) => (
              <Grid size={{ xs: 12, md: 4 }} key={d.tag}>
                <Box sx={{
                  height: '100%', borderRadius: 3.5, p: 4,
                  border: d.recommended ? `2px solid ${d.color}` : '1px solid #e2e8f0',
                  position: 'relative',
                  boxShadow: d.recommended ? `0 8px 40px ${d.color}18` : 'none',
                  transition: 'all 0.25s',
                  '&:hover': { boxShadow: `0 8px 40px ${d.color}20`, borderColor: d.color, transform: 'translateY(-3px)' },
                }}>
                  {d.recommended && (
                    <Box sx={{ position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)' }}>
                      <Chip label="Most Popular" size="small" sx={{ bgcolor: d.color, color: '#fff', fontWeight: 700, fontSize: '0.7rem' }} />
                    </Box>
                  )}
                  <Chip label={d.tag} size="small" sx={{ mb: 2.5, bgcolor: `${d.color}12`, color: d.color, fontWeight: 700, fontSize: '0.72rem', border: `1px solid ${d.color}30` }} />
                  <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', color: '#0f172a', mb: 1.5 }}>{d.title}</Typography>
                  <Typography sx={{ fontSize: '0.88rem', color: '#64748b', lineHeight: 1.8, mb: 3 }}>{d.desc}</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {d.points.map((pt) => (
                      <Box key={pt} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircle sx={{ fontSize: 16, color: d.color, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.82rem', color: '#334155', fontWeight: 500 }}>{pt}</Typography>
                      </Box>
                    ))}
                  </Box>
                  <Button onClick={() => navigate('/contact')} fullWidth sx={{ mt: 3.5, py: 1.2, borderRadius: '10px', fontWeight: 700, textTransform: 'none', fontSize: '0.88rem', color: d.recommended ? '#fff' : d.color, bgcolor: d.recommended ? d.color : `${d.color}10`, border: d.recommended ? 'none' : `1px solid ${d.color}30`, '&:hover': { bgcolor: d.recommended ? d.color : `${d.color}18` } }}>
                    Get a Quote
                  </Button>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* ── SECURITY & COMPLIANCE ── */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: { xs: 2, md: 5 }, background: 'linear-gradient(160deg, #0f172a 0%, #1e1b4b 60%, #1a0000 100%)' }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: 7 }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#fca5a5', mb: 1.5 }}>Security & Compliance</Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#fff', fontSize: { xs: '1.8rem', md: '2.4rem' }, mb: 1.5 }}>
              Built for Regulated Industries
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '1rem', maxWidth: 520, mx: 'auto' }}>
              GDPR-ready, HIPAA-aligned, and auditable — without add-ons or extra cost.
            </Typography>
          </Box>
          <Grid container spacing={2.5}>
            {complianceItems.map((c) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={c.label}>
                <Box sx={{
                  p: 3, borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  transition: 'all 0.22s',
                  '&:hover': { bgcolor: 'rgba(220,38,38,0.06)', borderColor: 'rgba(220,38,38,0.2)' },
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.2 }}>
                    <Box sx={{ color: '#fca5a5', display: 'flex', '& svg': { fontSize: 20 } }}>{c.icon}</Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#fff' }}>{c.label}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.45)', lineHeight: 1.7 }}>{c.desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* ── INDUSTRIES ── */}
      <Box sx={{ py: { xs: 7, md: 10 }, px: { xs: 2, md: 5 }, bgcolor: '#f8fafc' }}>
        <Box sx={{ maxWidth: 1000, mx: 'auto', textAlign: 'center' }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#94a3b8', mb: 1.5 }}>Industries</Typography>
          <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', fontSize: { xs: '1.8rem', md: '2.2rem' }, mb: 1.5 }}>
            Trusted Across Every Sector
          </Typography>
          <Typography sx={{ color: '#64748b', mb: 6, fontSize: '1rem', maxWidth: 480, mx: 'auto' }}>
            From healthcare to government — organizations in regulated industries choose CruvzMail for data control.
          </Typography>
          <Grid container spacing={2} justifyContent="center">
            {industries.map((ind) => (
              <Grid size={{ xs: 6, sm: 4, md: 3 }} key={ind.label}>
                <Box sx={{
                  p: 2.5, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#fff',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5,
                  transition: 'all 0.22s',
                  '&:hover': { borderColor: ind.color, boxShadow: `0 4px 20px ${ind.color}14`, transform: 'translateY(-2px)' },
                }}>
                  <Box sx={{ color: ind.color }}>{ind.icon}</Box>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a', textAlign: 'center' }}>{ind.label}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* ── SUPPORT TIERS ── */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: { xs: 2, md: 5 }, bgcolor: '#fff' }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: 7 }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#94a3b8', mb: 1.5 }}>Support</Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', fontSize: { xs: '1.8rem', md: '2.4rem' }, mb: 1.5 }}>
              Dedicated Support at Every Level
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '1rem', maxWidth: 480, mx: 'auto' }}>
              From email support to a named support engineer — we scale with your needs.
            </Typography>
          </Box>
          <Grid container spacing={3} alignItems="stretch">
            {supportTiers.map((tier) => (
              <Grid size={{ xs: 12, md: 4 }} key={tier.tier}>
                <Box sx={{
                  height: '100%', borderRadius: 3.5, p: 4,
                  border: tier.highlight ? `2px solid ${tier.color}` : '1px solid #e2e8f0',
                  boxShadow: tier.highlight ? `0 8px 40px ${tier.color}18` : 'none',
                  position: 'relative',
                  transition: 'all 0.25s',
                  '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 12px 40px ${tier.color}20` },
                }}>
                  {tier.highlight && (
                    <Box sx={{ position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)' }}>
                      <Chip label="Recommended" size="small" sx={{ bgcolor: tier.color, color: '#fff', fontWeight: 700, fontSize: '0.7rem' }} />
                    </Box>
                  )}
                  <Typography sx={{ fontWeight: 800, fontSize: '1.3rem', color: '#0f172a', mb: 0.5 }}>{tier.tier}</Typography>
                  <Box sx={{ display: 'flex', gap: 3, mb: 3, mt: 1.5 }}>
                    <Box>
                      <Typography sx={{ fontSize: '0.65rem', textTransform: 'uppercase', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.06em' }}>Response</Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: tier.color }}>{tier.response}</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '0.65rem', textTransform: 'uppercase', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.06em' }}>Uptime SLA</Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: tier.color }}>{tier.sla}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {tier.features.map((f) => (
                      <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircle sx={{ fontSize: 16, color: tier.color, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.83rem', color: '#334155', fontWeight: 500 }}>{f}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* ── FAQ ── */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: { xs: 2, md: 5 }, bgcolor: '#f8fafc' }}>
        <Box sx={{ maxWidth: 780, mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#94a3b8', mb: 1.5 }}>FAQ</Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', fontSize: { xs: '1.8rem', md: '2.3rem' } }}>
              Enterprise Questions, Answered
            </Typography>
          </Box>
          {faqs.map((faq, i) => (
            <Accordion key={i} elevation={0} disableGutters sx={{
              border: '1px solid #e2e8f0', borderRadius: '12px !important', mb: 1.5, overflow: 'hidden',
              '&:before': { display: 'none' },
              '&.Mui-expanded': { boxShadow: '0 4px 20px rgba(0,0,0,0.06)', borderColor: '#fecaca' },
            }}>
              <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#64748b' }} />} sx={{ px: 3, py: 0.5, '&.Mui-expanded': { bgcolor: '#fffbfb' } }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', color: '#0f172a', py: 0.5 }}>{faq.q}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 3, pb: 2.5, bgcolor: '#fffbfb' }}>
                <Typography sx={{ fontSize: '0.88rem', color: '#64748b', lineHeight: 1.85 }}>{faq.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Box>

      {/* ── CTA ── */}
      <Box sx={{ py: { xs: 10, md: 12 }, px: { xs: 2, md: 6 }, position: 'relative', overflow: 'hidden', bgcolor: '#0f172a' }}>
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(220,38,38,0.1) 0%, transparent 55%)', pointerEvents: 'none' }} />
        <Box sx={{ maxWidth: 1100, mx: 'auto', position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 7 }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#fca5a5', mb: 2 }}>Enterprise Sales</Typography>
              <Typography variant="h2" sx={{ fontWeight: 900, fontSize: { xs: '2rem', md: '3rem' }, color: '#fff', mb: 2.5, lineHeight: 1.15 }}>
                Ready to Move Your<br />Team to CruvzMail?
              </Typography>
              <Typography sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.55)', mb: 4, lineHeight: 1.85 }}>
                Volume pricing, dedicated resources, white-label branding, and a formal SLA — tailored to your organization's size and requirements.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                {['No lock-in contracts', 'Infrastructure managed by our team', 'Dedicated onboarding manager', 'Custom SLA available'].map((t) => (
                  <Box key={t} sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                    <CheckCircle sx={{ fontSize: 17, color: '#fca5a5', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.65)', fontWeight: 500 }}>{t}</Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <Box sx={{ bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, p: { xs: 3, md: 4 } }}>
                <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', color: '#fff', mb: 0.8 }}>Ready to get started?</Typography>
                <Typography sx={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.45)', mb: 3.5, lineHeight: 1.75 }}>
                  Our sales team typically responds within 2 business hours for enterprise inquiries.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Button onClick={() => navigate('/contact')} endIcon={<ArrowForward />} fullWidth sx={{ py: 1.6, borderRadius: '10px', fontWeight: 700, textTransform: 'none', fontSize: '1rem', color: '#fff', bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}>
                    Talk to Sales
                  </Button>
                  <Button onClick={() => navigate('/pricing')} fullWidth sx={{ py: 1.6, borderRadius: '10px', fontWeight: 700, textTransform: 'none', fontSize: '0.92rem', color: 'rgba(255,255,255,0.65)', border: '1px solid rgba(255,255,255,0.12)', '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' } }}>
                    Compare Pricing Plans
                  </Button>
                </Box>
                <Typography sx={{ textAlign: 'center', fontSize: '0.73rem', color: 'rgba(255,255,255,0.2)', mt: 2 }}>No commitment required</Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>

    </PublicLayout>
  );
};

export default EnterprisePage;
